import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

interface Subunternehmer {
  id: string;
  firmenname: string;
  ustId: string | null;
  steuernummer: string | null;
  email: string | null;
  telefon: string | null;
  adresseStrasse: string | null;
  adressePlz: string | null;
  adresseOrt: string | null;
  adresseLand: string;
  iban: string | null;
  bic: string | null;
  kontoinhaber: string | null;
  aktiv: boolean;
  notizen: string | null;
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute('/subunternehmer/')({
  beforeLoad: () => requireAccess('/api/v1/subunternehmer'),
  component: SubunternehmerPage,
});

function SubunternehmerPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['subunternehmer'],
    queryFn: () => api<{ items: Subunternehmer[] }>('/api/v1/subunternehmer'),
  });
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Subunternehmer"
        description="Externe Auftragnehmer (Garten, Spezial-Reparaturen). Portal-Logins werden separat unter Portale verwaltet."
        actions={
          <Button onClick={() => setShowForm((s) => !s)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? 'Abbrechen' : '+ Neuer Subunternehmer'}
          </Button>
        }
      />

      {showForm && (
        <SubCreateForm
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['subunternehmer'] });
          }}
        />
      )}

      {list.isLoading && <div className="text-slate-500">Lade…</div>}
      <ErrorBanner error={list.error} />

      {list.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Firma</th>
                <th className="px-4 py-2 text-left">USt-ID</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Telefon</th>
                <th className="px-4 py-2 text-left w-20">Aktiv</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.data.items.map((s) => (
                <SubRow key={s.id} sub={s} />
              ))}
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Noch kein Subunternehmer angelegt.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SubCreateForm({ onDone }: { onDone: () => void }) {
  const [firmenname, setFirmenname] = useState('');
  const [ustId, setUstId] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [iban, setIban] = useState('');
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { firmenname };
      if (ustId.trim()) body['ustId'] = ustId.trim();
      if (email.trim()) body['email'] = email.trim();
      if (telefon.trim()) body['telefon'] = telefon.trim();
      if (iban.trim()) body['iban'] = iban.trim();
      return api<{ item: Subunternehmer }>('/api/v1/subunternehmer', { method: 'POST', body });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">Firmenname</label>
          <Input required value={firmenname} onChange={(e) => setFirmenname(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">USt-ID (optional)</label>
          <Input
            value={ustId}
            onChange={(e) => setUstId(e.target.value)}
            placeholder="DE123456789"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Email (optional)</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Telefon (optional)</label>
          <Input value={telefon} onChange={(e) => setTelefon(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">IBAN (optional)</label>
          <Input value={iban} onChange={(e) => setIban(e.target.value)} />
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Subunternehmer anlegen'}
        </Button>
      </div>
    </form>
  );
}

function SubRow({ sub }: { sub: Subunternehmer }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [firmenname, setFirmenname] = useState(sub.firmenname);
  const [email, setEmail] = useState(sub.email ?? '');
  const [telefon, setTelefon] = useState(sub.telefon ?? '');
  const [iban, setIban] = useState(sub.iban ?? '');
  const [aktiv, setAktiv] = useState(sub.aktiv);
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string | boolean> = {};
      if (firmenname !== sub.firmenname) patch['firmenname'] = firmenname;
      if ((sub.email ?? '') !== email) patch['email'] = email;
      if ((sub.telefon ?? '') !== telefon) patch['telefon'] = telefon;
      if ((sub.iban ?? '') !== iban) patch['iban'] = iban;
      if (sub.aktiv !== aktiv) patch['aktiv'] = aktiv;
      if (Object.keys(patch).length === 0) return sub;
      return api<{ item: Subunternehmer }>(`/api/v1/subunternehmer/${sub.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['subunternehmer'] });
    },
    onError: (err) => setError(err),
  });

  const del = useMutation({
    mutationFn: () => api(`/api/v1/subunternehmer/${sub.id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['subunternehmer'] }),
    onError: (err) => setError(err),
  });

  if (!editing) {
    return (
      <tr className={`text-sm ${sub.aktiv ? 'text-slate-800' : 'text-slate-400'}`}>
        <td className="px-4 py-2 font-medium">{sub.firmenname}</td>
        <td className="px-4 py-2 text-slate-600">{sub.ustId ?? '–'}</td>
        <td className="px-4 py-2 text-slate-600">{sub.email ?? '–'}</td>
        <td className="px-4 py-2 text-slate-600">{sub.telefon ?? '–'}</td>
        <td className="px-4 py-2">
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs ${
              sub.aktiv ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {sub.aktiv ? 'aktiv' : 'inaktiv'}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              if (confirm(`Subunternehmer "${sub.firmenname}" wirklich löschen?`)) del.mutate();
            }}
          >
            Löschen
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="text-sm bg-brand-50/50">
      <td className="px-4 py-2">
        <Input value={firmenname} onChange={(e) => setFirmenname(e.target.value)} />
      </td>
      <td className="px-4 py-2 text-slate-600">{sub.ustId ?? '–'}</td>
      <td className="px-4 py-2">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} />
      </td>
      <td className="px-4 py-2">
        <Input value={telefon} onChange={(e) => setTelefon(e.target.value)} />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1 text-xs">
          <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          aktiv
        </label>
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            {error instanceof Error && (
              <span className="text-xs text-red-600">{error.message}</span>
            )}
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer">IBAN / Bankdaten</summary>
            <div className="mt-2 space-y-1">
              <Input
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                placeholder="DE89 3704 0044 0532 0130 00"
              />
            </div>
          </details>
        </div>
      </td>
    </tr>
  );
}
